import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WebcamRecorder } from "../components/WebcamRecorder";
import { CAPTURE_GROUPS, movementsByCaptureGroup } from "../../flex/movements";
import { processFlexClip } from "../../flex/capture";
import { computeFlexAge } from "../../flex/flexAge";
import { CURRENT_FLEX_VERSIONS, type FlexFinding, type FlexResult } from "../../flex/types";
import { PILOT } from "../../config";
import {
  saveSession,
  saveFlexResult,
  savePatient,
  getPatient,
  getFlexResult,
  listSessions,
  uuid,
} from "../../store/db";
import { CURRENT_VERSIONS, type Session } from "../../store/types";

export function FlexScreen() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [started, setStarted] = useState(false);
  const [groupIndex, setGroupIndex] = useState(0);
  const [findings, setFindings] = useState<FlexFinding[]>([]);
  const [frameTotal, setFrameTotal] = useState(0);
  const [durationTotal, setDurationTotal] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showRecorder, setShowRecorder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<Session[]>([]);

  useEffect(() => {
    listSessions().then((all) => setRecent(all.filter((s) => s.testType === "flex")));
  }, [started]);

  const group = CAPTURE_GROUPS[groupIndex];
  const isLast = groupIndex === CAPTURE_GROUPS.length - 1;

  function beginFlow() {
    setSessionId(uuid());
    setFindings([]);
    setFrameTotal(0);
    setDurationTotal(0);
    setGroupIndex(0);
    setStarted(true);
    setShowRecorder(true);
  }

  async function onRecorded(blob: Blob) {
    if (!sessionId) return;
    setShowRecorder(false);
    setBusy(true);
    setProgress(0);
    setError(null);
    try {
      const clip = await processFlexClip(blob, group.key, setProgress);
      const allFindings = [...findings, ...clip.findings];
      const allFrames = frameTotal + clip.frameCount;
      const allDuration = durationTotal + clip.durationMs;
      setFindings(allFindings);
      setFrameTotal(allFrames);
      setDurationTotal(allDuration);

      if (isLast) {
        await finish(sessionId, allFindings, allFrames, allDuration);
      } else {
        setGroupIndex((i) => i + 1);
        setShowRecorder(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function finish(
    sid: string,
    allFindings: FlexFinding[],
    allFrames: number,
    allDuration: number,
  ) {
    const patientId = await ensurePatient(code.trim() || "Unlabeled");
    const by = parseInt(birthYear, 10);
    const chronoAge = isFinite(by) && by > 1900 ? new Date().getFullYear() - by : null;

    // Most recent earlier Flex session for the same person, for the
    // "vs. letzte Messung" delta on the Beweglichkeitsalter.
    const allSessions = await listSessions(patientId);
    const priorFlex = allSessions
      .filter((s) => s.testType === "flex" && s.id !== sid)
      .sort((a, b) => b.recordedAt - a.recordedAt)[0];
    const priorResult = priorFlex ? await getFlexResult(priorFlex.id) : undefined;

    const flexAge = computeFlexAge(allFindings, chronoAge, priorResult?.flexAge.flexAge ?? null);

    const result: FlexResult = {
      sessionId: sid,
      recordedAt: Date.now(),
      findings: allFindings,
      flexAge,
      versions: CURRENT_FLEX_VERSIONS,
    };

    // Session.qc/view/fps are gait-specific fields from the shared schema
    // (see store/types.ts). A Flex session spans multiple views (frontal +
    // lateral capture groups) and has no gait-cycle concept, so these are
    // filled with honest best-effort placeholders rather than fantasy
    // values — the real per-movement confidence lives in FlexResult.findings,
    // which is what the UI actually reads. `view` is fixed to "frontal"
    // since most capture groups (3 of 5) use it; it is not semantically
    // meaningful for a Flex session and is not shown to the user.
    const session: Session = {
      id: sid,
      patientId,
      view: "frontal",
      milestone: "adhoc",
      recordedAt: Date.now(),
      videoRef: null,
      fps: 0,
      frameCount: allFrames,
      durationMs: allDuration,
      qc: {
        passed: allFindings.length > 0,
        fullBodyRatio: 1,
        meanConfidence: 1,
        fps: 0,
        detectedCycles: 0,
      },
      versions: CURRENT_VERSIONS,
      createdAt: Date.now(),
      testType: "flex",
    };

    await saveFlexResult(result);
    await saveSession(session);
    nav(`/flex/review/${sid}`);
  }

  async function ensurePatient(codeLabel: string): Promise<string> {
    const id = "pt-" + codeLabel.toLowerCase().replace(/\s+/g, "-");
    const existing = await getPatient(id);
    const now = Date.now();
    const by = parseInt(birthYear, 10);
    const demo = { birthYear: isFinite(by) && by > 1900 ? by : undefined };
    if (existing) {
      await savePatient({ ...existing, ...demo, updatedAt: now });
      return id;
    }
    await savePatient({ id, code: codeLabel, ...demo, createdAt: now, updatedAt: now });
    return id;
  }

  function cancelFlow() {
    setStarted(false);
    setShowRecorder(false);
    setSessionId(null);
  }

  const instructions = group
    ? Array.from(new Set(movementsByCaptureGroup(group.key).map((m) => m.instruction)))
    : [];
  const setupNote = group ? movementsByCaptureGroup(group.key)[0]?.setup : "";

  return (
    <>
      <div className="card hero">
        <h2>Beweglichkeit verstehen.</h2>
        <p>
          Spielerisches Flexibilitäts-Screening — 5 kurze Aufnahmen, ein
          Beweglichkeitsalter. Wellness-Tool, kein Medizinprodukt. Alle
          Aufnahmen bleiben auf diesem Gerät.
        </p>
      </div>

      {!started && (
        <div className="card">
          <strong>Neues Flex-Screening</strong>

          <label>Person — Name oder ID</label>
          <input
            type="text"
            placeholder="z. B. Anna M. oder HTO-014"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />

          <label>Geburtsjahr (optional)</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="z. B. 1985 — für das Beweglichkeitsalter"
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value)}
          />

          <label className="consent" style={{ marginTop: 18 }}>
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
            />
            Einwilligung der Person zur Aufnahme liegt vor
          </label>

          <button className="btn" style={{ marginTop: 12 }} disabled={!consentChecked} onClick={beginFlow}>
            Screening starten (5 Schritte)
          </button>
          {error && <p style={{ color: "var(--danger)" }}>{error}</p>}
        </div>
      )}

      {started && (
        <div className="card">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <strong>
              Schritt {groupIndex + 1} / {CAPTURE_GROUPS.length} · {group.label}
            </strong>
            <button className="btn secondary" style={{ width: "auto", padding: "6px 12px" }} onClick={cancelFlow}>
              Abbrechen
            </button>
          </div>
          <p className="muted" style={{ marginTop: 8 }}>{setupNote}</p>
          <ul className="checklist">
            {instructions.map((ins, i) => (
              <li key={i}>{ins}</li>
            ))}
          </ul>

          {busy && (
            <div style={{ marginTop: 12 }}>
              <div className="progress">
                <div style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
              <p className="muted">Wird ausgewertet… {Math.round(progress * 100)}%</p>
            </div>
          )}
          {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

          {showRecorder && !busy && (
            <WebcamRecorder
              view={group.view}
              onRecorded={onRecorded}
              onCancel={cancelFlow}
              countdownSec={3}
              maxDurationSec={10}
              setupText={setupNote}
              instructionText={instructions[0]}
              showSkeleton
              speak
            />
          )}
        </div>
      )}

      {!started && recent.length > 0 && (
        <div className="card">
          <strong>Zuletzt gescreent</strong>
          {recent.slice(0, 8).map((s) => (
            <div className="session-item" key={s.id}>
              <span className="muted">{new Date(s.recordedAt).toLocaleDateString()}</span>
              <a href={`#/flex/review/${s.id}`}>öffnen</a>
            </div>
          ))}
        </div>
      )}

      <p className="disclaimer">
        CADtomie Flex ist ein spielerisches Fitness-/Wellness-Tool — kein
        Medizinprodukt, keine klinische Diagnose und kein Ersatz für
        physiotherapeutische Untersuchung. Das Beweglichkeitsalter ist ein
        unterhaltsamer Vergleichswert, keine medizinische Kennzahl. Alle
        Videos und Daten bleiben auf diesem Gerät.
        <br />
        <a href={`mailto:${PILOT.feedbackEmail}?subject=CADtomie%20Flex%20Feedback`}>
          Feedback senden
        </a>
      </p>
    </>
  );
}
